import { useEffect, useMemo, useRef, useState } from 'react';
import customAxios from 'routes/Pages/util/Api';
import authHeader from 'services/auth/jwt/auth-header';
import { useMap } from './useMap';
import useAutoSave from './useAutoSave';
import { calculatePolygonAreaGoogle } from '@jumbo/utils/mapHelpers';
import { useDispatch } from 'react-redux';
import { FETCH_ERROR, FETCH_START, FETCH_SUCCESS } from '@jumbo/constants/ActionTypes';
import { useToast } from 'context/tostifyContext';
import { updateRecordModifiedDate } from 'routes/Pages/util/CommonAPIs';
import { toLocalISO } from 'routes/Pages/GenTask/components/SharedFormFields';

const useNewSiteAssessment = assessmentConfig => {
  const { showToast } = useToast();

  const [selectedType, setSelectedType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [formData, setFormData] = useState({});
  const [isFormCreated, setIsFormCreated] = useState(false);
  const [createdAssessmentId, setCreatedAssessmentId] = useState(null);
  const [siteRef, setSiteRef] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');

  // Add Owner drawer
  const [ownerDrawerOpen, setOwnerDrawerOpen] = useState(false);

  // Dropdown values
  const [dropdownOptions, setDropdownOptions] = useState({});
  const cacheRef = useRef({});
  const [ownersRaw, setOwnersRaw] = useState([]);

  const dispatch = useDispatch();

  // Samples
  const [samples, setSamples] = useState([]);
  const [samplesData, setSamplesData] = useState([]);
  const [subCompSampleData, setSubCompSampleData] = useState([]);

  // 🗺️ MAP STATES
  const [mapData, setMapData] = useState([]);
  const [openGoogleMapView, setOpenGoogleMapView] = useState(false);
  const [openRouteMapModal, setOpenRouteMapModal] = useState(false);
  const [routeDetails, setRouteDetails] = useState([]);

  // 🗺️ Initialize map hook
  const { mapState, mapActions } = useMap({
    mapData,
    sectionData: { siteAssessmentID: createdAssessmentId },
    open: openGoogleMapView,
    isMapLoaded: true,
  });

  const mainTableData = {
    siteAssessmentType: selectedType,
    siteAssessmentCategory: selectedCategory,
    ref: siteRef,
  };
  useAutoSave(createdAssessmentId, formData, isFormCreated, mainTableData);

  const handleOwnerChange = name => {
    setOwnerName(name);
    setLicenseNumber('');

    const licenses = ownersRaw
      .filter(
        o =>
          [o.crmContactFirst, o.crmContactLast]
            .filter(Boolean)
            .join(' ')
            .trim() === name,
      )
      .map(o => o.fellingLicenseNo)
      .filter(Boolean);

    setDropdownOptions(prev => ({ ...prev, LicenseNumbers: licenses }));
  };

  const handleTypeChange = type => {
    setSelectedType(type);
    setSelectedCategory('');
    setFormData({});
    setIsFormCreated(false);
    setOwnerName('');
    setLicenseNumber('');
  };

  const handleCategoryChange = category => {
    setSelectedCategory(category);
    setFormData({});
    setIsFormCreated(false);
    setOwnerName('');
    setLicenseNumber('');
  };

  const handleFieldChange = (name, value) => {
    setFormData(prev => {
      const updated = { ...prev, [name]: value };

      // Owner → License dependency
      if (name === 'ownerName') {
        const licenses = ownersRaw
          .filter(
            o =>
              [o.crmContactFirst, o.crmContactLast]
                .filter(Boolean)
                .join(' ')
                .trim() === value,
          )
          .map(o => o.fellingLicenseNo)
          .filter(Boolean);

        setDropdownOptions(prevOpts => ({
          ...prevOpts,
          LicenseNumbers: licenses,
        }));

        // reset license if owner changes
        updated.licenseNumber = '';
      }

      return updated;
    });
  };

  const createAssessment = async siteRef => {
    try {
      const matchedOwner = ownersRaw.find(
        o =>
          [o.crmContactFirst, o.crmContactLast]
            .filter(Boolean)
            .join(' ')
            .trim() === ownerName,
      );

      const crmId = matchedOwner?.crmId ?? null;

      const formData = new FormData();

      formData.append('siteAssessmentType', selectedType);
      formData.append('siteAssessmentCategory', selectedCategory);
      formData.append('ref', siteRef);
      formData.append('licenseNo', licenseNumber);
      formData.append('submissionDate', toLocalISO());
      formData.append('crmID', crmId);

      const res = await customAxios.post('/CRM/AddCRMSiteDetails', formData, { headers: authHeader() });

      if (res.status === 200) {
        setCreatedAssessmentId(res.data.newSiteAssessmentID);
        // Pre-populate owner fields so they appear filled in the full form
        setFormData(prev => ({ ...prev, ownerName, licenseNumber }));
        setIsFormCreated(true);
      }

      // setCreatedAssessmentId(492);
      // setIsFormCreated(true);
    } catch (err) {
      console.error('Assessment creation failed', err);
    }
  };

  // 🗺️ CREATE GEOFENCE - called after assessment creation
  const createGeofence = async (assessmentId, siteReference) => {
    if (!assessmentId) return;

    try {
      // Use default coordinates (Ireland center as fallback)
      const defaultCoords = { lat: 53.41291, lng: -8.24389 };

      const geoFencePayload = {
        siteAssessmentID: assessmentId,
        contractUniqueID: '',
        locationName: siteReference,
        latitude: defaultCoords.lat,
        longitude: defaultCoords.lng,
        radius: 500000,
        mapType: 'pin',
        geofenceType: 'site',
      };

      await customAxios.post('/Ops/AddGeofencingData', geoFencePayload, {
        headers: authHeader(),
      });

      console.log('✅ Geofence created for assessment ID:', assessmentId);
    } catch (error) {
      console.error('⚠️ Failed to create geofence:', error);
      // Don't throw - geofence creation failure shouldn't block the workflow
    }
  };

  useEffect(() => {
    if (!isFormCreated || !createdAssessmentId) return;

    const createDetailSkeletons = async () => {
      const headers = authHeader();

      await Promise.all([
        customAxios.post('/SiteDetails/AddSiteDetailsInfo', { siteAssessmentID: createdAssessmentId }, { headers }),
        customAxios.post('/SiteDetails/AddSiteDetailsHazards', { siteAssessmentID: createdAssessmentId }, { headers }),
        customAxios.post('/SiteDetails/AddSiteDetailsHarvesting', { siteAssessmentID: createdAssessmentId }, { headers }),
        customAxios.post('/SiteDetails/AddSiteDetailsChecklist', { siteAssessmentID: createdAssessmentId }, { headers }),
      ]);

      console.log('✅ Detail tables initialized');

      // Create geofence immediately after detail skeletons are initialized
      // This ensures geofence is created regardless of whether user opens the map
      await createGeofence(createdAssessmentId, siteRef);
    };

    createDetailSkeletons();
  }, [isFormCreated, createdAssessmentId]);

  // 🗺️ FETCH MAP DATA
  const fetchMapData = async () => {
    if (!createdAssessmentId) return;

    try {
      const response = await customAxios.get(`SiteDetails/GetSiteDetailsMapCoordinates/${createdAssessmentId}`, {
        headers: authHeader(),
      });
      setMapData(response.data || []);
    } catch (err) {
      console.error('Failed to fetch map data', err);
      setMapData([]);
    }
  };

  // 🗺️ SAVE MAP COORDINATES
  const saveMapCoordinates = async (feature, mapType, originalLabel) => {
    console.log('FEATURE ====', feature);
    const { mapLabel, displayName, hexColor, area, length } = feature;

    // Match all backend items with same label and type
    const backendItems = mapData.filter(item => item.mapType === mapType && item.mapLabel === originalLabel);

    const url =
      backendItems.length > 0 ? `/SiteDetails/EditSiteDetailsMapCoordinates` : `/SiteDetails/AddSiteDetailsMapCoordinates`;

    try {
      if (mapType === 'pin') {
        const payload = {
          id: backendItems[0]?.id ?? undefined, // first match if any
          siteAssessmentID: createdAssessmentId,
          mapLabel,
          displayName,
          hexColor,
          latitude: feature.latitude,
          longitude: feature.longitude,
          mapType,
          relatedTo: feature.relatedTo,
          area: 0,
        };

        const response = await customAxios.post(url, [payload], {
          headers: authHeader(),
        });

        if (response.status === 200) {
          fetchMapData();
        }
      } else {
        // Multiple coords — use IDs if available
        let areaValue = 0;
        if (mapType === 'polygon') {
          areaValue = calculatePolygonAreaGoogle(feature.coords);
        } else {
          areaValue = length;
        }
        const payloads = feature.coords.map((coord, idx) => ({
          id: backendItems[idx]?.id ?? undefined,
          siteAssessmentID: createdAssessmentId,
          mapLabel,
          displayName,
          hexColor,
          sortOrder: idx,
          latitude: coord.lat,
          longitude: coord.lng,
          mapType,
          area: areaValue,
        }));

        const response = await customAxios.post(url, payloads, {
          headers: authHeader(),
        });

        if (response.status === 200) {
          await updateRecordModifiedDate(createdAssessmentId);
          fetchMapData();
        }
      }
    } catch (err) {
      console.error('Map Save Error:', err);
    }
  };

  const updateMapCoordinates = async (feature, mapType) => {
    const { mapLabel, hexColor, coords, length } = feature;

    // Match backend items by label + type to get their IDs
    const backendItems = mapData.filter(item => item.mapType === mapType && item.mapLabel === mapLabel);

    let areaValue = 0;
    if (mapType === 'polygon') {
      areaValue = calculatePolygonAreaGoogle(coords);
    } else if (mapType === 'line') {
      areaValue = length;
    }

    if (mapType === 'pin') {
      const payload = [
        {
          id: backendItems[0]?.id ?? undefined,
          siteAssessmentID: createdAssessmentId,
          mapLabel,
          displayName: feature.displayName,
          hexColor,
          latitude: feature.latitude,
          longitude: feature.longitude,
          mapType,
          relatedTo: feature.relatedTo,
          area: 0,
        },
      ];

      try {
        await customAxios.post(`/SiteDetails/EditSiteDetailsMapCoordinates`, payload, {
          headers: authHeader(),
        });

        await updateRecordModifiedDate(createdAssessmentId);
      } catch (err) {
        console.error('Map coord update error:', err);
      }
      return;
    }

    const payloads = coords.map((coord, idx) => ({
      id: backendItems[idx]?.id ?? undefined,
      siteAssessmentID: createdAssessmentId,
      mapLabel,
      displayName: feature.displayName,
      hexColor,
      sortOrder: idx,
      latitude: coord.lat,
      longitude: coord.lng,
      mapType,
      area: areaValue,
    }));

    try {
      await customAxios.post(`/SiteDetails/EditSiteDetailsMapCoordinates`, payloads, {
        headers: authHeader(),
      });

      await updateRecordModifiedDate(createdAssessmentId);
    } catch (err) {
      console.error('Map coord update error:', err);
    }
  };

  // 🗺️ DELETE MAP COORDINATES
  const deleteMapCoordinates = async featureId => {
    if (!createdAssessmentId) return;

    try {
      await customAxios.delete(`/SiteAssessment/DeleteMapCoordinate/${featureId}`, {
        headers: authHeader(),
      });

      // Refresh map data
      await fetchMapData();
    } catch (err) {
      console.error('Failed to delete map coordinates', err);
      throw err;
    }
  };

  // 🗺️ OPEN MAP EDITOR
  const showSiteMap = () => {
    if (createdAssessmentId) {
      fetchMapData();
    }
    setOpenGoogleMapView(true);
  };

  // 🗺️ OPEN ROUTE VIEW
  const openRouteView = () => {
    if (createdAssessmentId) {
      fetchMapData();
      getCurrentSiteRouteDetails();
    }
    setOpenRouteMapModal(true);
  };

  // 🗺️ GET ROUTE DETAILS
  const getCurrentSiteRouteDetails = async () => {
    if (!createdAssessmentId) return;

    try {
      const response = await customAxios.get(`/CRM/GetCustomerLocations/?siteAssesmentID=${createdAssessmentId}`, {
        headers: authHeader(),
      });
      setRouteDetails(response.data || []);
    } catch (err) {
      console.error('Failed to fetch route details', err);
      setRouteDetails([]);
    }
  };

  // 🗺️ Fetch map data when form is created
  useEffect(() => {
    if (isFormCreated && createdAssessmentId) {
      fetchMapData();
      getCurrentSiteRouteDetails();
    }
  }, [isFormCreated, createdAssessmentId]);

  // 🔹 Get fields for current selection
  const currentFields = useMemo(() => {
    if (!selectedType || !selectedCategory || !isFormCreated) return [];
    return assessmentConfig[selectedType]?.fields[selectedCategory] || [];
  }, [selectedType, selectedCategory, isFormCreated, assessmentConfig]);

  // 🔹 Extract dropdown keys
  const dropdownKeys = useMemo(() => {
    const keys = currentFields.filter(f => f.type === 'dropdown' && f.dropdownKey).map(f => f.dropdownKey);
    return [...new Set(keys)];
  }, [currentFields]);

  // 🔹 Fetch dropdowns when form is created
  useEffect(() => {
    if (!isFormCreated) return;

    const keysToFetch = [...dropdownKeys.filter(k => !cacheRef.current[k]), 'Species'];
    if (keysToFetch.length === 0) return;

    const fetchDropdowns = async () => {
      try {
        const res = await customAxios.post('/GenTasks/GetDropdownListArray', keysToFetch, { headers: authHeader() });
        const newData = {};
        res.data.forEach(item => {
          cacheRef.current[item.keyName] = item.values;
          newData[item.keyName] = item.values;
        });

        setDropdownOptions(prev => ({ ...prev, ...newData }));
      } catch (err) {
        console.error('Dropdown fetch failed', err);
      }
    };

    fetchDropdowns();
  }, [dropdownKeys, isFormCreated]);

  useEffect(() => {
    fetchCustomerNames();
    fetchOwners();
  }, []);

  // Fetch and merge customer in dropdown
  const fetchCustomerNames = async () => {
    try {
      const res = await customAxios.get('/CRM/GetAllCustomerLocations', { headers: authHeader() });

      const names = (res.data || []).map(item => item.customerName).filter(Boolean);

      console.log('names');
      setDropdownOptions(prev => ({
        ...prev,
        CustomerNames: names,
      }));
    } catch (err) {
      console.error('Customer fetch failed', err);
    }
  };

  // Fetch and merge owners into dropdown
  const fetchOwners = async () => {
    try {
      const res = await customAxios.get('/CRM/GetOwnersDetails', { headers: authHeader() });

      setOwnersRaw(res.data || []);

      const ownerNames = [
        ...new Set(
          (res.data || []).map(o =>
            [o.crmContactFirst, o.crmContactLast]
              .filter(Boolean)
              .join(' ')
              .trim(),
          ),
        ),
      ];

      setDropdownOptions(prev => ({
        ...prev,
        OwnerNames: ownerNames,
        LicenseNumbers: [], // reset initially
      }));
    } catch (err) {
      console.error('Failed to fetch owners', err);
    }
  };

  // 🔹 Attach options to dropdown fields
  const fieldsWithOptions = useMemo(() => {
    return currentFields.map(field => {
      if (field.type === 'dropdown' && field.dropdownKey) {
        return {
          ...field,
          options: dropdownOptions[field.dropdownKey] || [],
        };
      }
      return field;
    });
  }, [currentFields, dropdownOptions]);

  // 🔹 Group by section
  const fieldsBySection = useMemo(() => {
    return fieldsWithOptions.reduce((acc, field) => {
      const section = field.section || 'General';
      if (!acc[section]) acc[section] = [];
      acc[section].push(field);
      return acc;
    }, {});
  }, [fieldsWithOptions]);

  // Samples
  const fetchSamples = async () => {
    dispatch({ type: FETCH_START });
    const url = `/SiteDetails/GetSiteDetailsSamples/${createdAssessmentId}`;
    const response = await customAxios.get(url, {
      headers: authHeader(),
    });
    if (response.status == 200) {
      dispatch({ type: FETCH_SUCCESS });
      // GET AREA IN HA
      const samplesData = await customAxios.get(`/SiteDetails/GetSiteDetailsSamplesData/${createdAssessmentId}`, {
        headers: authHeader(),
      });
      const areaMap = {};
      samplesData.data.forEach(item => {
        if (item.subCompartmentName) {
          areaMap[item.subCompartmentName] = parseFloat(item.areaInHa || 0);
        }
      });
      setSamples(response.data);
    } else {
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
    }
  };

  // const fetchSamplesData = async () => {
  //   dispatch({ type: FETCH_START });
  //   const url = `/SiteDetails/GetSiteDetailsSamplesData/${createdAssessmentId}`;
  //   const response = await customAxios.get(url, {
  //     headers: authHeader(),
  //   });
  //   if (response.status == 200) {
  //     dispatch({ type: FETCH_SUCCESS });
  //     console.log('response.data ===', response.data);
  //     setSamplesData(response.data);
  //   } else {
  //     dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
  //   }
  // };

  const fetchSubCompartmentSampleData = async () => {
    dispatch({ type: FETCH_START });
    const url = `/SiteDetails/GetSiteDetailsSubCompartmentSamples/${createdAssessmentId}`;
    const response = await customAxios.get(url, {
      headers: authHeader(),
    });
    if (response.status == 200) {
      dispatch({ type: FETCH_SUCCESS });
      console.log('response.data ===', response.data);
      setSubCompSampleData(response.data);
    } else {
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
    }
  };

  const handleSubCompSampleData = async (data, type) => {
    const url =
      type === 'add'
        ? '/SiteDetails/AddSiteDetailsSubCompartmentSamples/'
        : '/SiteDetails/EditSiteDetailsSubCompartmentSamples/';

    const payload = { ...data, siteAssessmentID: createdAssessmentId };

    const response = await customAxios.post(url, payload, {
      headers: authHeader(),
    });
    if (response.status == 200) {
      dispatch({ type: FETCH_SUCCESS });
      await fetchSubCompartmentSampleData();
    } else {
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
    }
  };

  const handleAction = async (data, type) => {
    let url;
    let response;

    dispatch({ type: FETCH_START });
    switch (type) {
      case 'add':
        const { id, ...payload } = data;
        const finalPayload = [{ ...payload, siteAssessmentID: createdAssessmentId }];
        url = `/SiteDetails/AddSiteDetailsSamples/`;
        response = await customAxios.post(url, finalPayload, {
          headers: authHeader(),
        });
        break;

      case 'update':
        const updatePayload = { ...data, siteAssessmentID: createdAssessmentId };
        url = `/SiteDetails/EditSiteDetailsSamples/`;
        response = await customAxios.post(url, updatePayload, {
          headers: authHeader(),
        });
        break;

      case 'delete':
        const delPayload = { id: data.id };
        url = `/SiteDetails/DeleteSiteDetailsSamples/`;
        response = await customAxios.post(url, delPayload, {
          headers: authHeader(),
        });
        break;
    }

    if (response.status == 200) {
      dispatch({ type: FETCH_SUCCESS });
      await fetchSamples();
    } else {
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
    }
  };

  // Add new owner
  const saveNewOwner = async payload => {
    try {
      const res = await customAxios.post('/CRM/AddContact', payload, {
        headers: authHeader(),
      });

      if (res.status === 200) {
        showToast('Owner Added Successfully', 'success');
        setOwnerName(payload.crmContactFirst);
        setLicenseNumber(payload.fellingLicenseNo);
        setOwnersRaw([
          ...ownersRaw,
          {
            crmContactFirst: payload.crmContactFirst,
            crmContactLast: '',
            licenseNumber: payload.fellingLicenseNo,
            crmId: res.data,
          },
        ]);
      }
    } catch (error) {
      console.log(error);
      showToast('Failed to add', 'error');
    }
  };

  const handleMapClose = async () => {
    try {
      let coords = null;

      if (mapState?.pins?.length > 0) {
        const entrancePin = mapState?.pins?.find(pin => pin.mapLabel?.startsWith('Entrance'));
        if (entrancePin) {
          coords = { lat: entrancePin.latitude, lng: entrancePin.longitude };
        }
      }

      // Fallback to center if no entrance pin found
      if (!coords) {
        coords = mapState?.center || { lat: 53.41291, lng: -8.24389 };
      }

      // Update the existing geofence with actual map coordinates
      const geoFencePayload = {
        siteAssessmentID: createdAssessmentId,
        contractUniqueID: '',
        locationName: siteRef,
        latitude: coords.lat,
        longitude: coords.lng,
        radius: 500000,
        mapType: 'pin',
        geofenceType: 'site',
      };

      await customAxios.post('/Ops/AddGeofencingData', geoFencePayload, {
        headers: authHeader(),
      });

      console.log('✅ Geofence updated with map coordinates');
    } catch (error) {
      console.error('⚠️ Failed to update geofence on map close:', error);
    } finally {
      mapActions.setMode(null);
      setOpenGoogleMapView(false);
    }
  };

  return {
    // Basic form state
    selectedType,
    selectedCategory,
    formData,
    currentFields: fieldsWithOptions,
    fieldsBySection,
    isFormCreated,
    createdAssessmentId,
    setSiteRef,
    siteRef,
    ownerName,
    setOwnerName: handleOwnerChange,
    licenseNumber,
    setLicenseNumber,

    // Form actions
    setSelectedType: handleTypeChange,
    setSelectedCategory: handleCategoryChange,
    handleFieldChange,
    createAssessment,

    // 🗺️ Map state
    mapData,
    mapState,
    openGoogleMapView,
    setOpenGoogleMapView,
    openRouteMapModal,
    setOpenRouteMapModal,
    routeDetails,

    // 🗺️ Map actions
    mapActions,
    saveMapCoordinates,
    updateMapCoordinates,
    deleteMapCoordinates,
    showSiteMap,
    openRouteView,
    getCurrentSiteRouteDetails,
    fetchMapData,

    dropdownOptions,
    samples,
    // samplesData,
    subCompSampleData,
    handleAction,
    handleSubCompSampleData,

    ownerDrawerOpen,
    setOwnerDrawerOpen,
    saveNewOwner,
    handleMapClose,
  };
};

export default useNewSiteAssessment;
